import { useEffect, useState } from 'react';
import { Link as RouterLink } from '@tanstack/react-router';
import {
  PlatformRole,
  platformRoleAtLeast,
  type AdminConsumption,
  type AdminConsumptionSeries,
  type AdminMonitoring,
  type AdminOrgSummary,
  type AdminUsageAnalytics,
} from '@ddp/shared';
import Loading from '@carbon/react/es/components/Loading/Loading';
import { InlineNotification } from '@carbon/react/es/components/Notification/Notification';

import { apiFetch } from '../api/client.js';
import { useAuthStore } from '../auth/store.js';
import { PageHeader } from '../components/PageHeader.js';
import { ConsumptionTrend } from '../features/admin/ConsumptionTrend.js';
import { DayAreaChart } from '../features/admin/DayAreaChart.js';
import { formatBytes, formatCompact, quotaRatio } from '../features/admin/format.js';

/** Seuil d'alerte de quota de stockage : au-delà, l'organisation est signalée à l'exploitant. */
const QUOTA_WARN = 0.9;

/**
 * Console d'administration plateforme (ADR-0025). Réservée au super-administrateur : voit *toutes*
 * les organisations. Le back garde la surface (404 sinon) ; le front la masque aussi pour ne pas
 * afficher une page vouée à l'échec, et gate le lien d'accès sur le rôle plateforme.
 */
export function AdminConsolePage() {
  const currentUser = useAuthStore((state) => state.currentUser);
  // Accès en lecture dès SUPPORT (ADR-0025, incrément 3) ; le back garde de toute façon la surface.
  const canView = currentUser
    ? platformRoleAtLeast(currentUser.platformRole, PlatformRole.SUPPORT)
    : false;

  const [orgs, setOrgs] = useState<AdminOrgSummary[]>([]);
  const [monitoring, setMonitoring] = useState<AdminMonitoring | null>(null);
  const [consumption, setConsumption] = useState<AdminConsumption | null>(null);
  const [series, setSeries] = useState<AdminConsumptionSeries | null>(null);
  const [usage, setUsage] = useState<AdminUsageAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!canView) {
      setLoading(false);
      return;
    }
    let active = true;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const [o, m, c, s, u] = await Promise.all([
          apiFetch<AdminOrgSummary[]>('/admin/organizations'),
          apiFetch<AdminMonitoring>('/admin/monitoring'),
          apiFetch<AdminConsumption>('/admin/consumption'),
          apiFetch<AdminConsumptionSeries>('/admin/consumption/series'),
          apiFetch<AdminUsageAnalytics>('/admin/analytics'),
        ]);
        if (!active) return;
        setOrgs(o);
        setMonitoring(m);
        setConsumption(c);
        setSeries(s);
        setUsage(u);
      } catch (err) {
        if (active) setError(err instanceof Error ? err.message : 'Chargement impossible.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [canView]);

  if (!canView) {
    return (
      <section className="ddp-page__section">
        <PageHeader title="Administration" />
        <InlineNotification
          kind="info"
          title="Accès réservé"
          subtitle="La console d’administration est réservée aux exploitants de la plateforme."
          lowContrast
          hideCloseButton
        />
      </section>
    );
  }

  if (loading) {
    return (
      <section className="ddp-page__section">
        <Loading withOverlay={false} description="Chargement…" />
      </section>
    );
  }

  // Organisations dont le stockage approche ou dépasse le quota : signalées en tête (alerte).
  const quotaAlerts = orgs.filter((org) => {
    const ratio = quotaRatio(org.storageUsedBytes, org.storageQuotaBytes);
    return ratio !== null && ratio >= QUOTA_WARN;
  });

  return (
    <section className="ddp-page__section ddp-admin">
      <PageHeader
        title="Administration"
        lead="Vue transverse de la plateforme — toutes les organisations. Chaque consultation est journalisée."
      />

      {error && (
        <InlineNotification
          kind="error"
          title="Erreur"
          subtitle={error}
          lowContrast
          onClose={() => setError(null)}
        />
      )}

      {monitoring && <MonitoringPanel monitoring={monitoring} />}

      {usage && <UsagePanel usage={usage} />}

      {consumption && <ConsumptionPanel consumption={consumption} series={series} />}

      {quotaAlerts.length > 0 && (
        <InlineNotification
          kind="warning"
          lowContrast
          hideCloseButton
          title={`Quota de stockage bientôt atteint (≥ ${Math.round(QUOTA_WARN * 100)} %)`}
          subtitle={`${quotaAlerts.length} organisation${quotaAlerts.length > 1 ? 's' : ''} : ${quotaAlerts.map((o) => o.name).join(', ')}.`}
        />
      )}

      <h2 className="ddp-admin__subtitle">
        Organisations <span className="ddp-admin__count">{orgs.length}</span>
      </h2>
      <div className="ddp-admin__table-wrap">
        <table className="ddp-admin__table">
          <thead>
            <tr>
              <th scope="col">Organisation</th>
              <th scope="col" className="ddp-admin__num">
                Membres
              </th>
              <th scope="col" className="ddp-admin__num">
                Vidéos
              </th>
              <th scope="col" className="ddp-admin__num">
                Modèles
              </th>
              <th scope="col">Stockage</th>
            </tr>
          </thead>
          <tbody>
            {orgs.map((org) => {
              const ratio = quotaRatio(org.storageUsedBytes, org.storageQuotaBytes);
              return (
                <tr key={org.id}>
                  <td>
                    <RouterLink
                      to="/admin/organisations/$organizationId"
                      params={{ organizationId: org.id }}
                      className="ddp-admin__org-link"
                    >
                      {org.name}
                    </RouterLink>
                    <span className="ddp-admin__org-slug">{org.slug}</span>
                  </td>
                  <td className="ddp-admin__num">{org.memberCount}</td>
                  <td className="ddp-admin__num">{org.videoCount}</td>
                  <td className="ddp-admin__num">{org.modelCount}</td>
                  <td>
                    <div className="ddp-admin__storage">
                      <span className="ddp-admin__storage-text">
                        {formatBytes(org.storageUsedBytes)} / {formatBytes(org.storageQuotaBytes)}
                      </span>
                      {ratio !== null && (
                        <span
                          className={`ddp-admin__bar ddp-admin__bar--${ratio >= 0.9 ? 'danger' : ratio >= 0.7 ? 'warn' : 'ok'}`}
                        >
                          <span style={{ width: `${Math.round(ratio * 100)}%` }} />
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/** Bandeau monitoring : pastilles de santé des services + profondeur des files. */
function MonitoringPanel({ monitoring }: { monitoring: AdminMonitoring }) {
  const services: [string, boolean][] = [
    ['Base de données', monitoring.services.database],
    ['Stockage objet', monitoring.services.storage],
    ['File de traitement', monitoring.services.queue],
  ];
  const queues = Object.entries(monitoring.queues);

  return (
    <section className="ddp-admin__monitoring" aria-label="État des services">
      <div className="ddp-admin__health">
        {services.map(([label, ok]) => (
          <span key={label} className={`ddp-admin__pill ddp-admin__pill--${ok ? 'ok' : 'danger'}`}>
            <span className="ddp-admin__dot" aria-hidden="true" />
            {label}
          </span>
        ))}
      </div>
      {queues.length > 0 && (
        <dl className="ddp-admin__queues">
          {queues.map(([name, depth]) => (
            <div key={name} className="ddp-admin__queue">
              <dt>{name}</dt>
              <dd>{depth}</dd>
            </div>
          ))}
        </dl>
      )}
    </section>
  );
}

/**
 * Consommation (ADR-0025, incrément 2) : jetons IA et transfert de données par organisation, sur la
 * fenêtre glissante renvoyée par l'API. Le transfert est une **borne haute** (octets autorisés à
 * l'émission des URLs de lecture), pas le trafic réellement servi — rappelé sous le tableau.
 */
function ConsumptionPanel({
  consumption,
  series,
}: {
  consumption: AdminConsumption;
  series: AdminConsumptionSeries | null;
}) {
  return (
    <section className="ddp-admin__consumption" aria-label="Consommation">
      <h2 className="ddp-admin__subtitle">
        Consommation
        <span className="ddp-admin__window">{consumption.windowDays} derniers jours</span>
      </h2>
      <div className="ddp-admin__totals">
        <div className="ddp-admin__total">
          <span className="ddp-admin__total-value">
            {formatCompact(consumption.totals.aiTokens)}
          </span>
          <span className="ddp-admin__total-label">jetons IA</span>
        </div>
        <div className="ddp-admin__total">
          <span className="ddp-admin__total-value">
            {formatBytes(consumption.totals.dataTransferBytes)}
          </span>
          <span className="ddp-admin__total-label">transfert (borne haute)</span>
        </div>
      </div>

      {/* Tendance : deux graphes mono-série (échelles séparées, jamais un double axe). */}
      {series && series.days.length > 0 && (
        <div className="ddp-admin__trends">
          <ConsumptionTrend
            title="Jetons IA par jour"
            metric="aiTokens"
            days={series.days}
            format={formatCompact}
            accent="tokens"
          />
          <ConsumptionTrend
            title="Transfert par jour"
            metric="dataTransferBytes"
            days={series.days}
            format={formatBytes}
            accent="transfer"
          />
        </div>
      )}

      {consumption.organizations.length > 0 && (
        <div className="ddp-admin__table-wrap">
          <table className="ddp-admin__table">
            <thead>
              <tr>
                <th scope="col">Organisation</th>
                <th scope="col" className="ddp-admin__num">
                  Jetons IA
                </th>
                <th scope="col" className="ddp-admin__num">
                  Transfert
                </th>
              </tr>
            </thead>
            <tbody>
              {consumption.organizations.map((org) => (
                <tr key={org.organizationId}>
                  <td>
                    <span className="ddp-admin__org-name">{org.name}</span>
                    <span className="ddp-admin__org-slug">{org.slug}</span>
                  </td>
                  <td className="ddp-admin__num">{formatCompact(org.aiTokens)}</td>
                  <td className="ddp-admin__num">{formatBytes(org.dataTransferBytes)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="ddp-admin__note">
        Transfert : octets autorisés à l’ouverture d’une vidéo (borne haute) — l’octet réel est
        servi par le stockage objet, jamais par l’API.
      </p>
    </section>
  );
}

/** Libellés français des événements d'usage (identifiants techniques → langue produit). */
const FEATURE_LABEL: Record<string, string> = {
  PAGE_VIEWED: 'Pages vues',
  VIDEO_UPLOAD_STARTED: 'Uploads démarrés',
  VIDEO_UPLOAD_COMPLETED: 'Uploads aboutis',
  VIDEO_UPLOAD_FAILED: 'Uploads échoués',
  VIDEO_PLAYBACK_STARTED: 'Lectures',
  ANNOTATION_CREATED: 'Annotations créées',
  SEARCH_PERFORMED: 'Recherches',
  ASSISTANT_QUERIED: 'Questions à l’assistant',
};

/** Seuil sous lequel le taux de complétion d'upload passe en alerte (friction). */
const COMPLETION_WARN = 0.8;

/**
 * Analytique d'usage (ADR-0026, incrément 3) : utilisateurs actifs (total + courbe), tunnel d'upload
 * (mesure la friction) et top fonctionnalités. Agrégats transverses, journalisés côté API.
 */
function UsagePanel({ usage }: { usage: AdminUsageAnalytics }) {
  const { started, completed, failed } = usage.uploadFunnel;
  const completionRate = started > 0 ? completed / started : null;
  const maxFeature = Math.max(1, ...usage.topFeatures.map((f) => f.count));

  return (
    <section className="ddp-admin__consumption" aria-label="Analytique d’usage">
      <h2 className="ddp-admin__subtitle">
        Analytique d’usage
        <span className="ddp-admin__window">{usage.windowDays} derniers jours</span>
      </h2>

      <div className="ddp-admin__totals">
        <div className="ddp-admin__total">
          <span className="ddp-admin__total-value">{formatCompact(usage.activeUsers)}</span>
          <span className="ddp-admin__total-label">utilisateurs actifs</span>
        </div>
        <div className="ddp-admin__total">
          <span className="ddp-admin__total-value">{formatCompact(usage.eventTotal)}</span>
          <span className="ddp-admin__total-label">événements</span>
        </div>
      </div>

      <div className="ddp-admin__trends">
        <DayAreaChart
          title="Utilisateurs actifs par jour"
          points={usage.activeDaily.map((d) => ({ date: d.date, value: d.users }))}
          format={(v) => formatCompact(v)}
          accent="users"
          totalLabel={() => `${usage.activeUsers} au total`}
        />

        <div>
          <h3 className="ddp-chart__title">Tunnel d’upload</h3>
          <div className="ddp-usage__funnel">
            <div className="ddp-usage__step">
              <span className="ddp-usage__step-value">{started}</span>
              <span className="ddp-usage__step-label">démarrés</span>
            </div>
            <div className="ddp-usage__step">
              <span className="ddp-usage__step-value">{completed}</span>
              <span className="ddp-usage__step-label">aboutis</span>
            </div>
            <div className="ddp-usage__step">
              <span className="ddp-usage__step-value">{failed}</span>
              <span className="ddp-usage__step-label">échoués</span>
            </div>
            {completionRate !== null && (
              <div
                className={`ddp-usage__step ddp-usage__step--rate${
                  completionRate < COMPLETION_WARN ? ' ddp-usage__step--warn' : ''
                }`}
              >
                <span className="ddp-usage__step-value">{Math.round(completionRate * 100)} %</span>
                <span className="ddp-usage__step-label">taux de complétion</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <h3 className="ddp-chart__title">Fonctionnalités les plus utilisées</h3>
      {usage.topFeatures.length > 0 ? (
        <dl className="ddp-usage__bars">
          {usage.topFeatures.map((feature) => (
            <div key={feature.name} className="ddp-usage__bar">
              <dt>{FEATURE_LABEL[feature.name] ?? feature.name}</dt>
              <div className="ddp-usage__bar-track" aria-hidden="true">
                <span
                  className="ddp-usage__bar-fill"
                  style={{ width: `${Math.round((feature.count / maxFeature) * 100)}%` }}
                />
              </div>
              <dd>{formatCompact(feature.count)}</dd>
            </div>
          ))}
        </dl>
      ) : (
        <p className="ddp-admin__note">Aucun événement d’usage sur la période.</p>
      )}
    </section>
  );
}
